/**
 * InCallAssessmentMenu — one-click "send assessment to client" button for
 * the bottom call toolbar.
 *
 * The same logic lives in CallClinicalPanel's Tools tab, but pushing an
 * assessment from there required two levels of nested navigation
 * (right-side panel toggle → Tools tab inside Clinical) which therapists
 * found hard to reach mid-call. This puts it next to the mic / camera /
 * hangup buttons where the therapist's attention already is.
 *
 * Click → popover lists the therapist's effective assessments
 * (defaults ∪ grants from the 3-tier governance resolver). Click an item
 * to push it to the client immediately. Closes the popover and shows
 * a Snackbar toast.
 */

import React, { useEffect, useState } from 'react';
import {
  IconButton, Tooltip, Menu, MenuItem, ListItemText, ListItemIcon,
  Typography, Box, CircularProgress, Snackbar, Alert, Chip,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SendIcon from '@mui/icons-material/Send';
import api from '../../services/api';

export default function InCallAssessmentMenu({ liveCallId, token }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [pending, setPending] = useState({});
  const [toast, setToast] = useState(null);

  const open = Boolean(anchorEl);

  // Load lazily on first open so we don't hit the API for every call.
  useEffect(() => {
    if (!open || tests.length || loading) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await api.get('/assessment-governance/therapist/available', token);
        const list = res?.testDefinitions || res?.data?.testDefinitions || [];
        if (!cancelled) setTests(list);
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.error || err.message || 'Could not load assessments');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, token, tests.length, loading]);

  const send = async (testKey) => {
    if (!liveCallId) {
      setToast({ message: 'Call not connected yet — try again in a moment.', severity: 'warning' });
      return;
    }
    setPending(prev => ({ ...prev, [testKey]: true }));
    try {
      await api.post(`/calls/${liveCallId}/in-session/trigger-assessment`, { assessmentKey: testKey }, token);
      setAnchorEl(null);
      setToast({ message: `${testKey.toUpperCase()} sent to client.`, severity: 'success' });
    } catch (err) {
      setToast({
        message: err?.response?.data?.error || err.message || 'Could not send assessment',
        severity: 'error',
      });
    } finally {
      setPending(prev => ({ ...prev, [testKey]: false }));
    }
  };

  return (
    <>
      <Tooltip title="Send assessment to client">
        <span>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disabled={!liveCallId}
            sx={{
              bgcolor: 'grey.200',
              color: 'text.primary',
              '&:hover': { bgcolor: 'grey.300' },
              width: 56,
              height: 56,
            }}
          >
            <AssignmentIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{ sx: { minWidth: 320, maxWidth: 420, maxHeight: 360 } }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">Send assessment to client</Typography>
          <Typography variant="caption" color="text.secondary">
            Appears on their screen now. Results post back automatically.
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {loadError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">{loadError}</Alert>
          </Box>
        )}

        {!loading && !loadError && tests.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Alert severity="info">
              No assessments available. Ask your supervisor or admin to grant assessments to your profile.
            </Alert>
          </Box>
        )}

        {tests.map(t => (
          <MenuItem
            key={t.testKey}
            onClick={() => send(t.testKey)}
            disabled={!!pending[t.testKey]}
          >
            <ListItemIcon>
              {pending[t.testKey] ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={500}>
                    {t.shortName || t.testKey.toUpperCase()}
                  </Typography>
                  {t.population && (
                    <Chip size="small" label={t.population} variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              }
              secondary={
                <Typography variant="caption" color="text.secondary" noWrap>
                  {t.fullName || t.description}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast && (
          <Alert severity={toast.severity || 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        )}
      </Snackbar>
    </>
  );
}
